import React, { useMemo } from 'react'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import SlideBase from '../SlideBase'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import Group from 'components/utils/Group'
import type { LoyaltyData } from '../../../types/wrapped'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Label from 'LuxKit/Label'
import { getImageUrl } from 'lib/image/imageUtils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useWrappedStoryState } from 'contexts/Wrapped'
import InteractiveZone from '../../InteractiveZone'
import TextButton from 'LuxKit/Button/TextButton'
import LELogoSvg from './LELogoSvg'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import { EmptyArray } from 'lib/array/arrayUtils'

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`

type LoyaltyTier = LoyaltyData['tier']

const TIER_IMAGE_IDS: Record<LoyaltyTier, string> = {
  bronze: 'societe-coint-bronze',
  silver: 'societe-coin-silver',
  gold: 'societe-coin-gold',
  platinum: 'societe-coin-platinum',
}

const TIER_DISPLAY_NAMES: Record<LoyaltyTier, string> = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
}

const BENEFIT_TITLES: Record<App.LoyaltyBenefitType, string> = {
  hotel_upgrade: 'Free room and package upgrades',
  airport_transfer: 'Free airport chauffeur service',
  lounge_pass: 'Airport lounge passes',
  discounted_insurance: 'Travel protection discount',
  phone_support: 'Priority phone support 24/7',
  tour_exp_credit: 'Tour optional extra credits',
  travelshoot_credit: 'TravelShoot voucher',
  early_access: 'Early access to exclusive offers',
  hidden_access: 'Hidden offers access',
}

/**
 * Converts a display name (e.g. 'Platinum') to a tier key (e.g. 'platinum')
 */
function displayNameToTierKey(displayName: string): App.LoyaltyTier | undefined {
  const lowerName = displayName.toLowerCase()
  if (lowerName === 'bronze' || lowerName === 'silver' || lowerName === 'gold' || lowerName === 'platinum') {
    return lowerName as App.LoyaltyTier
  }
  return undefined
}

/**
 * Gets the target tier for benefits display based on tier progression
 */
function getTargetTier(
  currentTier: App.LoyaltyTier,
  tierProgression: LoyaltyData['tierProgression'],
): App.LoyaltyTier {
  if (!tierProgression) return currentTier

  if (tierProgression.type === 'unlock' && tierProgression.name) {
    const nextTier = displayNameToTierKey(tierProgression.name)
    if (nextTier) return nextTier
  }

  // For 'retain' type or if name conversion fails, use current tier
  return currentTier
}

/**
 * Gets the benefit titles for a given tier from the loyalty config
 */
function getTierBenefitTitles(
  config: App.LoyaltyConfig,
  tier: App.LoyaltyTier,
): Array<string> {
  const tierBenefits = config.regionTierBenefits[tier] ?? []
  const enabledBenefits = config.regionEnabledBenefits

  return tierBenefits
    .filter(benefit => enabledBenefits[benefit.code])
    .map(benefit => BENEFIT_TITLES[benefit.code])
    .filter((title): title is string => Boolean(title))
}

const Background = styled.div`
  position: absolute;
  inset: 0;

  &.bronze {
    background:
      linear-gradient(180deg, transparent 0%, ${props => props.theme.palette.product.loyalty.bronze.darkBackground} 50%),
      radial-gradient(126.92% 35.81% at 50% 31.09%, ${props => props.theme.palette.product.loyalty.bronze.normalBackground} 0%, ${props => props.theme.palette.product.loyalty.bronze.darkBackground} 100%);
  }

  &.silver {
    background:
      linear-gradient(180deg, transparent 0%, ${props => props.theme.palette.product.loyalty.silver.darkBackground} 50%),
      radial-gradient(126.92% 35.81% at 50% 31.09%, ${props => props.theme.palette.product.loyalty.silver.normalBackground} 0%, ${props => props.theme.palette.product.loyalty.silver.darkBackground} 100%);
  }

  &.gold {
    background:
      linear-gradient(180deg, transparent 0%, ${props => props.theme.palette.product.loyalty.gold.darkBackground} 50%),
      radial-gradient(126.92% 35.81% at 50% 31.09%, ${props => props.theme.palette.product.loyalty.gold.normalBackground} 0%, ${props => props.theme.palette.product.loyalty.gold.darkBackground} 100%);
  }

  &.platinum {
    background:
      linear-gradient(180deg, transparent 0%, ${props => props.theme.palette.product.loyalty.platinum.darkBackground} 50%),
      radial-gradient(126.92% 35.81% at 50% 31.09%, ${props => props.theme.palette.product.loyalty.platinum.normalBackground} 0%, ${props => props.theme.palette.product.loyalty.platinum.darkBackground} 100%);
  }
`

const LELogoBackground = styled.div`
  position: absolute;
  left: 50%;
  top: 38%;
  transform: translate(-50%, -50%);
  width: 70%;
  min-width: ${rem(100)};
  max-width: ${rem(325)};
  aspect-ratio: 325 / 437;
  pointer-events: none;

  svg {
    width: 100%;
    height: 100%;
  }
`

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(12)};
  padding: 0 0 ${rem(40)};
  border-radius: ${rem(20)};
  flex: 1;
`

const SocieteLogo = styled.img`
  min-width: ${rem(50)};
  max-width: ${rem(126)};
  height: auto;
`

const CoinContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`

const CoinImage = styled.img`
  width: 35%;
  min-width: ${rem(100)};
  max-width: ${rem(190)};
  height: auto;
  object-fit: contain;
  animation: ${float} 3s ease-in-out infinite;
  /* Enable GPU acceleration for smooth animation */
  will-change: transform;
  transform: translateZ(0);

  ${mediaQueryUp.tablet} {
    width: 50%;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    will-change: auto;
  }
`

const BenefitsCard = styled.div`
  width: 100%;
  padding: ${rem(16)} ${rem(20)};
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${rem(8)};
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const CheckIconWrapper = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  color: ${props => props.theme.palette.neutral.default.one};
`

function LoyaltySlide() {
  const { data } = useWrappedStoryState()
  const loyalty = data?.loyalty

  // Memoize expensive image URL generation
  const societeLogo = useMemo(() => getImageUrl('societe-logo-white', {
    format: 'png',
    quality: 'best',
    dpr: 2,
    width: 126,
  }), [])

  const coinImage = useMemo(() => {
    if (!loyalty) return ''
    return getImageUrl(TIER_IMAGE_IDS[loyalty.tier], {
      format: 'png',
      width: 190,
    })
  }, [loyalty])

  const [config] = useLoyaltyConfig()

  const { tierProgression } = loyalty ?? {}

  const tierName = useMemo(() => {
    if (!loyalty) return ''
    if (tierProgression?.type === 'unlock' && tierProgression.name) {
      return tierProgression.name
    }
    return TIER_DISPLAY_NAMES[loyalty.tier]
  }, [loyalty, tierProgression])

  const targetTier = useMemo(() => {
    if (!loyalty) return undefined
    return getTargetTier(loyalty.tier, tierProgression)
  }, [loyalty, tierProgression])

  const benefitTitles = useMemo(() => {
    if (!config || !targetTier) return EmptyArray as Array<string>
    return getTierBenefitTitles(config, targetTier)
  }, [config, targetTier])

  if (!data || !loyalty) {
    return null
  }

  return (
    <SlideBase
      gap={60}
      verticalAlign="space-between"
      background={
        <Background className={loyalty.tier}>
          <LELogoBackground>
            <LELogoSvg />
          </LELogoBackground>
        </Background>
      }
    >
      <Card>
        <SocieteLogo src={societeLogo} alt="Société" loading="lazy" />
        <Caption colour="neutral-one" variant="small" format="uppercase">
          loyalty rewards
        </Caption>
        <CoinContainer>
          <CoinImage src={coinImage} alt={`${loyalty.tier} tier coin`} loading="lazy" />
        </CoinContainer>
        <Group direction="vertical" gap={8} horizontalAlign="center" style={{ textAlign: 'center' }}>
          <Heading variant="heading5" colour="neutral-one">
            <b>In 2025, you earned a total of</b>
          </Heading>
          <Heading variant="heading2alt" colour="neutral-one">
            <i>
              {loyalty.points > 0 && <>{loyalty.points.toLocaleString()} Points</>}
              {loyalty.points > 0 && loyalty.statusCredits > 0 && <> &<br /></>}
              {loyalty.statusCredits > 0 && <>{loyalty.statusCredits} Status Credits</>}
            </i>
          </Heading>
        </Group>
        <Label kind="primary" variant="dark">
          Top {loyalty.percentile}% of our travellers
        </Label>
      </Card>

      {tierProgression && benefitTitles.length > 0 && (
        <BenefitsCard>
          <BodyText variant="medium" colour="neutral-one">
            You need {tierProgression.creditsNeeded} status credits and {tierProgression.nightsNeeded} nights to {tierProgression.type === 'unlock' ? 'unlock' : 'remain'} <b>{tierName}</b> to enjoy these benefits:
          </BodyText>
          <Group direction="vertical" gap={8} wrap="wrap" verticalAlign="start">
            {benefitTitles.slice(0, 3).map((benefitTitle) => (
              <Group key={benefitTitle} direction="horizontal" gap={8} verticalAlign="start">
                <CheckIconWrapper>
                  <LineCheckIcon size="XS" />
                </CheckIconWrapper>
                <BodyText variant="medium" colour="neutral-one">
                  {benefitTitle}
                </BodyText>
              </Group>
            ))}
            <InteractiveZone>
              <TextButton shape="round" fit="full-width" kind="secondary" to="/societe" target="_blank">Learn more</TextButton>
            </InteractiveZone>
          </Group>
        </BenefitsCard>
      )}
    </SlideBase>
  )
}

export default React.memo(LoyaltySlide)
